import { attachmentSchema } from "@/api/attachment";
import { useGetSelectionFormData } from "@/api/cms";
import { useGetSections, useUpdateSectionsPositions } from "@/api/section";
import { useGetLayout, useUpdateLayout } from "@/api/selection";
import {
  manageLayoutFormSchema,
  ManageLayoutFullMenu,
} from "@/components/cms/selection/LayoutSelector";
import { DataTable } from "@/components/ui/datatable";
import { Form, FormField } from "@/components/ui/form";
import { error, success, toast } from "@/hooks/use-toast";
import { getMainLanguage } from "@/i18n";
import CMSLayout from "@/layout/CMSLayout";
import { LayoutNonNullable } from "@/lib/cms/section-types";
import { zodResolver } from "@hookform/resolvers/zod";
import { ColumnDef } from "@tanstack/react-table";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

const sectionWithPositionSchema = z.object({
  id: z.string(),
  title: z.string().optional(),
  position: z.number(),
  thumbnail: attachmentSchema.pick({
    mediumPublicUrl: true,
  }),
});

type SectionWithPosition = z.infer<typeof sectionWithPositionSchema>;

const formSchema = manageLayoutFormSchema.extend({
  sections: z.array(sectionWithPositionSchema),
});
type FormType = z.infer<typeof formSchema>;

const ManageLayout = () => {
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { data: sections } = useGetSections();
  const { mutate: updateSectionsPositions } = useUpdateSectionsPositions();
  const { mutate: updateLayout } = useUpdateLayout();
  const { data: layout } = useGetLayout();

  const { data: selectionPageData } = useGetSelectionFormData();

  const defaultValues: FormType = useMemo(
    () => ({
      layout: {
        numberOfColumns: layout?.numberOfColumns ?? 2,
        alignment: layout?.alignment ?? "CENTER",
        captionAlignment: layout?.captionAlignment ?? "LEFT",
      },
      sections:
        sections?.map((s, index) => {
          const content = s.content[getMainLanguage()];
          return {
            id: s.id,
            title: content?.title,
            position: index + 1,
            thumbnail: s.thumbnail,
          };
        }) ?? [],
    }),
    [layout, sections],
  );

  const form = useForm<FormType>({
    resolver: zodResolver(formSchema),
    defaultValues: defaultValues,
  });

  useEffect(() => {
    if (layout) {
      form.reset(defaultValues);
    }
  }, [layout, defaultValues, form]);

  const onSubmit = (submittedData: FormType) => {
    updateLayout(submittedData.layout as LayoutNonNullable);
    updateSectionsPositions(submittedData.sections.map((s) => s.id));

    toast(
      success({
        description: t("cms.selection.manageLayout.success.updateLayout"),
      }),
    );

    navigate("/cms/selection");
  };

  const datatableColumns = useMemo(
    (): ColumnDef<SectionWithPosition>[] => [
      {
        id: "thumbnail",
        header: "",
        size: 300,
        cell: ({ row }) => {
          const section = row.original;

          return (
            <img
              className="aspect-square object-contain"
              src={section.thumbnail.mediumPublicUrl}
              alt={section.title}
            />
          );
        },
      },
      {
        header: t("cms.selection.manageLayout.table.column.position"),
        accessorKey: "position",
      },
      {
        size: 3000,
        header: t("cms.selection.manageLayout.table.column.title"),
        accessorKey: "title",
      },
    ],
    [t],
  );

  return (
    <CMSLayout
      formIds={["layout"]}
      pageName={t("cms.selection.manageLayout.title")}
      breadcrumbs={[
        {
          label:
            selectionPageData?.[getMainLanguage()]?.pageName ??
            t("cmsTabSelection"),
          to: "/cms/selection",
        },
        {
          label: t("cms.selection.manageLayout.title"),
        },
      ]}
    >
      <Form {...form}>
        <form
          id="layout"
          onSubmit={form.handleSubmit(onSubmit, (errors) => {
            toast(
              error({ description: JSON.stringify(errors), duration: 5000 }),
            );
          })}
          className="flex flex-col gap-12 w-full p-8"
        >
          <div className="flex flex-col gap-6">
            <p className="text-lg font-semibold text-gray-950">
              {t("cms.selection.manageLayout.globalLayout.title")}
            </p>
            <ManageLayoutFullMenu control={form.control} />
          </div>
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-2">
              <p className="text-lg font-semibold text-gray-950">
                {t("cms.selection.manageLayout.organize.title")}
              </p>
              <p className="text-sm font-medium text-gray-700">
                {t("cms.selection.manageLayout.organize.description")}
              </p>
            </div>
            <FormField
              control={form.control}
              name="sections"
              render={({ field }) => (
                <DataTable
                  enableDragRows
                  onRowDragged={(d) =>
                    field.onChange(
                      d.newData.map((s, index) => ({
                        ...s,
                        position: index + 1,
                      })),
                    )
                  }
                  columns={datatableColumns}
                  data={field.value}
                />
              )}
            />
          </div>
        </form>
      </Form>
    </CMSLayout>
  );
};

export default ManageLayout;
